import type { LucideIcon } from "lucide-react";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";

/**
 * Shared visual shape for the Topbar's "Válido até", "Simulado começa em" and
 * Concurso-selector controls: an optional small icon tile, a caption line and
 * a stronger value line beneath it, with an optional trailing chevron for the
 * one caller that opens a menu.
 *
 * Purely presentational — no hooks, no data fetching, no onClick. Wiring
 * (click handlers, menus, navigation) is the caller's job.
 */
export interface StatusBadgeProps {
  /** Lucide icon component rendered inside the 26×26 tile. Omit to render no tile and reclaim its space (no reserved gap). */
  icon?: LucideIcon;
  /** Classes for the icon tile's own background/foreground, e.g. "bg-accent-blue/[0.12] text-accent-blue-ink". Defaults to a neutral token pair when omitted. */
  iconClassName?: string;
  /** Small label above the value, e.g. "Válido até". */
  caption: string;
  /** The stronger line beneath the caption, e.g. a formatted date or "CBMGO". */
  value: ReactNode;
  /** Renders a trailing chevron — used by the one caller (certame selector) that opens a menu. */
  chevron?: boolean;
  /** Extra classes merged onto the root control, e.g. so a caller can add interactive/fill styling. */
  className?: string;
}

export function StatusBadge({
  icon: Icon,
  iconClassName,
  caption,
  value,
  chevron,
  className = "",
}: StatusBadgeProps) {
  return (
    <div
      className={`flex h-[42px] items-center gap-2 overflow-hidden rounded-chip border border-edge bg-surface px-3 ${className}`}
    >
      {Icon && (
        <span
          aria-hidden
          className={`inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-control ${
            iconClassName || "bg-surface-muted text-ink-secondary"
          }`}
        >
          <Icon size={14} strokeWidth={2} />
        </span>
      )}

      <div className="min-w-0 flex-1">
        <span className="block truncate text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
          {caption}
        </span>
        <span className="block truncate text-sm font-semibold text-ink">{value}</span>
      </div>

      {chevron && <ChevronDown size={14} aria-hidden className="shrink-0 text-ink-secondary" />}
    </div>
  );
}
